import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

export default function ModelComparisonChart({ data }) {
  const formattedData = React.useMemo(() => {
    if (!data) return [];

    const metrics = [
      { name: 'Structure', key: 'structured_score' },
      { name: 'Coverage', key: 'point_coverage_score' },
      { name: 'Length', key: 'note_length_score' },
      { name: 'Faithfulness', key: 'faithfulness_score' }
    ];

    return metrics.map(metric => {
      const metricData = { name: metric.name };

      Object.entries(data.stats.models).forEach(([modelName, modelStats]) => {
        const value = modelStats[metric.key];
        metricData[modelName] = value !== undefined && value !== null ? Number(value) : null;
      });

      return metricData;
    });
  }, [data]);

  // Custom tooltip to format values
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 shadow-lg rounded">
          <p className="font-semibold mb-1">{label}</p>
          {payload.map((entry, index) => (
            <p key={`item-${index}`} style={{ color: entry.color }} className="text-sm">
              {`${entry.name}: ${entry.value !== null ? entry.value.toFixed(2) : 'N/A'}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Generate dynamic colors for each model
  const modelColors = React.useMemo(() => {
    if (!data) return {};

    const colors = [
      '#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#0088FE', '#00C49F',
      '#FFBB28', '#FF8042', '#a4de6c', '#d0ed57'
    ];

    return Object.keys(data.stats.models).reduce((acc, model, index) => {
      acc[model] = colors[index % colors.length];
      return acc;
    }, {});
  }, [data]);

  const formatYAxis = (value) => {
    return value.toFixed(1);
  };

  if (!data || Object.keys(data.stats.models).length === 0) {
    return (
      <div className="bg-white p-6 rounded-lg shadow text-center">
        <p className="text-gray-500">No model comparison data available</p>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow h-full">
      <h3 className="text-lg font-semibold mb-4 text-ktx-navy">Model-to-Model Comparison</h3>
      <div style={{ width: '100%', height: 'calc(100% - 40px)' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={formattedData}
            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis domain={[0, 1]} tickFormatter={formatYAxis} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            {Object.keys(data.stats?.models || {}).map((model) => (
              <Bar
                key={model}
                dataKey={model}
                fill={modelColors[model]}
                name={model}
                label={{
                  position: 'top',
                  formatter: (value) => value !== null ? value.toFixed(2) : '',
                  fontSize: 10,
                  fill: '#666',
                  offset: 10
                }}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}